import React, { useEffect, useState } from "react";
import { Crown, Trophy } from "lucide-react";
import { Layout } from "../components/Layout";
import api from "../lib/api";
import { useVisitor } from "../context/VisitorAuthContext";
import { TV, TvKicker, TvHeader } from "../components/TriviaTheme";
import { ChampionCardButton } from "../components/ChampionCard";

const BOARDS = [["uk", "UK Chart Three"], ["us", "US Chart Three"], ["sport", "Sport Three"]];
const fmt = (iso) => new Date(iso + "T00:00:00Z").toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" });

const Cell = ({ c, testid, board, week }) => c ? (
  <div data-testid={testid}>
    <p className="font-ui font-semibold text-sm text-[#161514] flex items-center gap-1.5"><Crown className="w-3.5 h-3.5 text-[#B8922E]" /> {c.nickname}</p>
    <p className="font-ui text-xs text-[#77716A] mt-0.5">{c.points} pts · {c.accuracy}% · {c.players} {c.players === 1 ? "player" : "players"}</p>
    <div className="mt-2">
      <ChampionCardButton nickname={c.nickname} board={board} week={week} points={c.points} accuracy={c.accuracy} players={c.players} testid={`${testid}-card`} />
    </div>
  </div>
) : <p className="font-ui text-xs text-[#A9A399]" data-testid={testid}>No named winner</p>;

export default function TriviaChampions() {
  const { isMember } = useVisitor();
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/trivia/champions").then((r) => setD(r.data)).catch(() => setD({ weeks: [], most_titles: [] })); }, []);
  return (
    <Layout>
      <div className={`font-ui ${TV.bg} min-h-screen`} data-testid="champions-page">
        <TvHeader title="Trivia Vault" subtitle="Hall of Champions" isMember={isMember} back="/trivia" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 pt-6 sm:pt-10 pb-24">
          <TvKicker>Weekly winners</TvKicker>
          <h1 className="font-tv-serif font-bold text-[#161514] text-[34px] sm:text-5xl leading-[1.02] mt-4">Hall of Champions.</h1>
          <p className="font-ui text-[15px] sm:text-lg text-[#77716A] mt-3 max-w-xl">Every week's number one on the UK Chart Three, US Chart Three and Sport Three boards.</p>
          {d && d.most_titles.length > 0 && (
            <div className={`${TV.green} p-5 mt-7`} data-testid="most-titles">
              <p className="tv-caps text-white/70">Most titles</p>
              <div className="flex flex-wrap gap-2 mt-3">
                {d.most_titles.map((t, i) => (
                  <span key={t.nickname} data-testid={`most-titles-${i}`} className="inline-flex items-center gap-1.5 bg-white/10 border border-white/20 rounded-full px-3 py-1.5 font-ui text-sm font-semibold">
                    <Trophy className="w-3.5 h-3.5 text-[#E8D5A6]" /> {t.nickname} · {t.titles}
                  </span>
                ))}
              </div>
            </div>
          )}
          {!d ? <p className="font-ui text-sm text-[#77716A] mt-8">Loading…</p> : d.weeks.length === 0 ? (
            <div className={`${TV.card} p-8 mt-7 text-center`} data-testid="champions-empty">
              <Crown className="w-8 h-8 mx-auto text-[#B8922E]" />
              <p className="font-ui text-sm sm:text-base text-[#57524B] mt-3">No champions crowned yet. The first ones are named after the first full week of play.</p>
            </div>
          ) : (
            <div className="space-y-3 mt-7" data-testid="champions-list">
              {d.weeks.map((w) => (
                <div key={w.week} className={`${TV.card} p-5`} data-testid={`champions-week-${w.week}`}>
                  <div className="flex items-center justify-between gap-3">
                    <p className="font-ui font-bold text-[#161514]">Week of {fmt(w.week)}</p>
                    {w.leader && <span className={`${TV.pillMint} font-ui text-xs font-semibold px-3 py-1`}>{w.leader.toUpperCase()} won UK vs US</span>}
                  </div>
                  <div className="grid sm:grid-cols-3 gap-4 mt-4">
                    {BOARDS.map(([b, name]) => (
                      <div key={b}>
                        <p className="tv-caps !text-[10px] text-[#77716A] mb-1.5">{name}</p>
                        <Cell c={w.boards[b]} board={b} week={w.week} testid={`champion-${w.week}-${b}`} />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
